import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ArrowLeft, ArrowRight, User, Mail, Lock, Check, CheckCircle, Phone,
} from 'lucide-react';
import { useUI, useAuth } from '../context/AppContext';
import {
  sendEmailSignInCode,
  confirmEmailSignInCode,
  sendPhoneSignInCode,
  confirmPhoneSignInCode,
  resetPhoneRecaptcha,
  warmPhoneRecaptcha,
  describePhoneAuthError,
  startInstructorEmailRegistration,
  startInstructorPhoneRegistration,
  getPendingEmailSignIn,
  clearPendingEmailSignIn,
} from '../services/auth';
import { markExplicitLoginIntent, isPortalAccessGranted } from '../utils/historySession';
import ThemeToggle from '../components/ui/ThemeToggle';
import Logo from '../components/ui/Logo';
import Modal from '../components/ui/Modal';
import StudentSignupForm from '../components/auth/StudentSignupForm';
import EmailSuggestion from '../components/ui/EmailSuggestion';
import {
  AUTH_INTENT,
  INSTRUCTOR_REGISTRATION_ENABLED,
  INSTRUCTOR_REGISTRATION_CLOSED_HINT,
  STUDENT_REGISTRATION_ENABLED,
  STUDENT_REGISTRATION_CLOSED_MESSAGE,
  STUDENT_EMAIL_ONLY_MESSAGE,
  REGISTRATION_CLOSED_MESSAGE,
  STORAGE_KEYS,
  USER_ROLE,
} from '../utils/constants';
import { normalizePhoneTR } from '../utils/phoneUtils';

const detectLoginKind = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  if (trimmed.includes('@')) return 'email';
  if (normalizePhoneTR(trimmed) || /^[\d\s()+.-]{6,}$/.test(trimmed)) return 'phone';
  return null;
};

const LoginScreen = () => {
  const {
    setCurrentPage, showToast, isDarkMode, setIsDarkMode,
    goToPortal, setIsAuthSubmitting,
  } = useUI();
  const { loggedInUser, isLoading: authLoading, firebaseUser: authFirebaseUser } = useAuth();
  const [authMode, setAuthMode] = useState('login');
  const [authRole, setAuthRole] = useState(USER_ROLE.INSTRUCTOR);
  const [loginId, setLoginId] = useState('');
  const [registerLoginId, setRegisterLoginId] = useState('');
  const [name, setName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  /** 6. hane girildiğinde (elle veya iOS/Android otomatik doldurma) ayrıca "Doğrula"ya basmak gerekmesin */
  const handleOtpInput = (e) => {
    const next = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(next);
    if (next.length !== 6 || otpCode.length === 6 || isProcessing) return;
    const { form } = e.target;
    setTimeout(() => {
      if (!form?.isConnected) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }, 0);
  };
  const [otpStep, setOtpStep] = useState('enter');
  const [otpKind, setOtpKind] = useState(null);
  const [phoneConfirmation, setPhoneConfirmation] = useState(null);
  const submittingRef = useRef(false);

  const loginKind = useMemo(() => detectLoginKind(loginId), [loginId]);
  const isStudentLogin = authMode === 'login' && authRole === USER_ROLE.STUDENT;
  const isStudentSignup = authMode === 'register' && authRole === USER_ROLE.STUDENT;
  const registerKind = useMemo(() => detectLoginKind(registerLoginId), [registerLoginId]);
  const wantsPhoneCode = authMode === 'login'
    ? !isStudentLogin && loginKind === 'phone'
    : authRole === USER_ROLE.INSTRUCTOR && registerKind === 'phone';

  useEffect(() => {
    if (wantsPhoneCode) warmPhoneRecaptcha();
  }, [wantsPhoneCode, otpStep]);

  const resetOtpState = () => {
    clearPendingEmailSignIn();
    setOtpStep('enter');
    setOtpKind(null);
    setOtpCode('');
    setPhoneConfirmation(null);
    resetPhoneRecaptcha();
  };

  useEffect(() => {
    if (!authLoading && loggedInUser && isPortalAccessGranted(loggedInUser.id)) {
      goToPortal(loggedInUser.role);
    }
  }, [authLoading, loggedInUser, goToPortal]);

  /* Firebase oturumu varken (çıkış yapılmamış) SMS istemeden portala dön */
  useEffect(() => {
    if (authLoading || loggedInUser || !authFirebaseUser) return;
    if (!authFirebaseUser.phoneNumber && !authFirebaseUser.email) return;
    const resumeKey = 'finly_resume_auth';
    if (window.sessionStorage.getItem(resumeKey) === authFirebaseUser.uid) return;
    window.sessionStorage.setItem(resumeKey, authFirebaseUser.uid);
    markExplicitLoginIntent();
    window.location.reload();
  }, [authLoading, loggedInUser, authFirebaseUser]);

  useEffect(() => {
    const savedIntent = window.localStorage.getItem(STORAGE_KEYS.AUTH_INTENT);
    const savedRole =
      window.localStorage.getItem(STORAGE_KEYS.INTENDED_ROLE) ||
      window.localStorage.getItem(STORAGE_KEYS.LAST_ROLE);

    if (savedIntent === AUTH_INTENT.REGISTER) {
      window.localStorage.removeItem(STORAGE_KEYS.AUTH_INTENT);
      if (savedRole === USER_ROLE.STUDENT && STUDENT_REGISTRATION_ENABLED) {
        setAuthMode('register');
        setAuthRole(USER_ROLE.STUDENT);
        return;
      }
      if (!INSTRUCTOR_REGISTRATION_ENABLED || savedRole === USER_ROLE.STUDENT) {
        setAuthMode('login');
        setAuthRole(savedRole === USER_ROLE.INSTRUCTOR ? USER_ROLE.INSTRUCTOR : USER_ROLE.STUDENT);
        return;
      }
      setAuthMode('register');
      setAuthRole(USER_ROLE.INSTRUCTOR);
      return;
    }

    if (savedIntent === AUTH_INTENT.LOGIN) {
      setAuthMode('login');
      window.localStorage.removeItem(STORAGE_KEYS.AUTH_INTENT);
    }

    if (savedRole === USER_ROLE.INSTRUCTOR || savedRole === USER_ROLE.STUDENT) {
      setAuthRole(savedRole);
    }

    const pendingEmail = getPendingEmailSignIn();
    if (pendingEmail) {
      setAuthMode('login');
      setLoginId(pendingEmail.email);
      setOtpKind('email');
      setOtpStep('code');
    }
  }, []);

  /**
   * Kod doğrulandıktan sonra düğme kilitli kalır; profil yüklenince ya da yükleme
   * profilsiz bitince (hesap yok, bağlantı hatası, oturum kapandı) tekrar açılır.
   */
  const prevAuthRef = useRef({ user: authFirebaseUser, loading: authLoading });
  useEffect(() => {
    const prev = prevAuthRef.current;
    prevAuthRef.current = { user: authFirebaseUser, loading: authLoading };
    const settledWithoutProfile = !loggedInUser && !authLoading
      && (prev.loading || (prev.user && !authFirebaseUser));
    if (loggedInUser || settledWithoutProfile) {
      submittingRef.current = false;
      setIsProcessing(false);
      setIsAuthSubmitting(false);
    }
  }, [loggedInUser, authLoading, authFirebaseUser, setIsAuthSubmitting]);

  const describeAuthError = (err) => {
    if (err.message === 'INVALID_EMAIL') return 'Geçerli bir e-posta adresi girin.';
    if (err.message === 'INVALID_EMAIL_CODE') return 'E-posta kodu hatalı.';
    if (err.message === 'EMAIL_CODE_PENDING') return 'Önce e-posta kodu isteyin.';
    if (err.message === 'EMAIL_CODE_EXPIRED') return 'Kodun süresi doldu. Yeni kod isteyin.';
    if (err.message === 'EMAIL_CODE_RATE_LIMIT') {
      return err.messageDetail || 'Çok fazla deneme. Biraz bekleyip yeni kod isteyin.';
    }
    if (err.message === 'INVALID_LOGIN_ID') {
      return 'E-posta adresi veya cep telefonu girin.';
    }
    if (err.code === 'auth/email-already-in-use') return 'Bu mail zaten kullanımda.';
    if (err.message === 'REGISTRATION_CLOSED') return REGISTRATION_CLOSED_MESSAGE;
    return describePhoneAuthError(err);
  };

  const handleInstructorRegister = async () => {
    if (!INSTRUCTOR_REGISTRATION_ENABLED) {
      return showToast(REGISTRATION_CLOSED_MESSAGE, 'error');
    }
    if (authRole !== USER_ROLE.INSTRUCTOR) {
      return showToast(STUDENT_REGISTRATION_CLOSED_MESSAGE, 'error');
    }
    if (!name.trim()) {
      return showToast('Ad soyad girin.', 'error');
    }
    if (!agreedToTerms) {
      return showToast('Kullanıcı sözleşmesini kabul edin.', 'error');
    }
    const kind = detectLoginKind(registerLoginId);
    if (!kind) {
      return showToast('E-posta adresi veya cep telefonu girin.', 'error');
    }
    setIsProcessing(true);
    window.localStorage.setItem(STORAGE_KEYS.INTENDED_ROLE, USER_ROLE.INSTRUCTOR);
    try {
      if (kind === 'email') {
        await startInstructorEmailRegistration({
          email: registerLoginId,
          name: name.trim(),
        });
        setOtpKind('email');
        setOtpStep('code');
        setOtpCode('');
        showToast('Hesabınız oluşturuldu. Giriş için e-postanıza gelen kodu girin.');
      } else {
        const confirmation = await startInstructorPhoneRegistration({
          phone: registerLoginId,
          name: name.trim(),
        });
        setPhoneConfirmation(confirmation);
        setOtpKind('phone');
        setOtpStep('code');
        setOtpCode('');
        showToast('Hesabınız oluşturuldu. Giriş için SMS kodunu girin.');
      }
    } catch (err) {
      console.error('Kayıt Hatası:', err);
      if (err.message === 'ACCOUNT_EXISTS') {
        setAuthMode('login');
        setLoginId(registerLoginId);
        resetOtpState();
        showToast('Bu bilgilerle hesabınız zaten var. Giriş kodu isteyerek devam edin.', 'error');
      } else {
        showToast(describeAuthError(err), 'error');
      }
    }
    setIsProcessing(false);
  };

  const handleSendEmailCode = async (emailValue) => {
    setIsProcessing(true);
    window.localStorage.setItem(STORAGE_KEYS.INTENDED_ROLE, authRole);
    try {
      await sendEmailSignInCode(emailValue);
      setOtpKind('email');
      setOtpStep('code');
      setOtpCode('');
      showToast('Giriş kodu e-postanıza gönderildi.');
    } catch (err) {
      console.error('Auth Hatası:', err);
      showToast(describeAuthError(err), 'error');
    }
    setIsProcessing(false);
  };

  const handleSendPhoneCode = async (phoneValue) => {
    setIsProcessing(true);
    window.localStorage.setItem(STORAGE_KEYS.INTENDED_ROLE, authRole);
    try {
      const confirmation = await sendPhoneSignInCode(phoneValue);
      setPhoneConfirmation(confirmation);
      setOtpKind('phone');
      setOtpStep('code');
      setOtpCode('');
      showToast('SMS kodu gönderildi.');
    } catch (err) {
      console.error('Auth Hatası:', err);
      showToast(describeAuthError(err), 'error');
    }
    setIsProcessing(false);
  };

  const handleConfirmOtpCode = async () => {
    setIsProcessing(true);
    setIsAuthSubmitting(true);
    window.localStorage.setItem(STORAGE_KEYS.INTENDED_ROLE, authRole);
    markExplicitLoginIntent();
    try {
      if (otpKind === 'email') {
        const emailValue = authMode === 'register' ? registerLoginId : loginId;
        await confirmEmailSignInCode(emailValue, otpCode);
      } else {
        await confirmPhoneSignInCode(phoneConfirmation, otpCode);
      }
      return true;
    } catch (err) {
      console.error('Auth Hatası:', err);
      showToast(describeAuthError(err), 'error');
    }
    setIsProcessing(false);
    setIsAuthSubmitting(false);
    return false;
  };

  const handleAuthAction = async (e) => {
    e.preventDefault();
    // Çift dokunuşta state henüz güncellenmemiş olabilir; ikinci doğrulama deneme hakkı yakar
    if (submittingRef.current) return;
    submittingRef.current = true;
    let awaitingAuth = false;
    try {
      awaitingAuth = (await runAuthAction()) === true;
    } finally {
      if (!awaitingAuth) submittingRef.current = false;
    }
  };

  const runAuthAction = async () => {
    if (otpStep === 'code') {
      return handleConfirmOtpCode();
    }
    if (authMode === 'register') {
      return handleInstructorRegister();
    }
    const kind = detectLoginKind(loginId);
    if (kind === 'email') {
      return handleSendEmailCode(loginId);
    }
    if (isStudentLogin) {
      return showToast(STUDENT_EMAIL_ONLY_MESSAGE, 'error');
    }
    if (kind === 'phone') {
      return handleSendPhoneCode(loginId);
    }
    showToast(describeAuthError(new Error('INVALID_LOGIN_ID')), 'error');
  };

  const submitLabel = (() => {
    if (otpStep === 'code') return 'Kodu Doğrula';
    if (authMode === 'register') {
      if (registerKind === 'phone') return 'SMS ile Kayıt Ol';
      if (registerKind === 'email') return 'E-posta ile Kayıt Ol';
      return 'Kayıt Ol';
    }
    if (loginKind === 'email') return 'E-posta Kodu Gönder';
    if (loginKind === 'phone' && !isStudentLogin) return 'SMS Kodu Gönder';
    return 'Devam Et';
  })();

  return (
    <>
      <div className="app-shell min-h-[100dvh] h-[100dvh] w-full max-w-[100vw] flex font-instructor app-auth-bg animate-instructor-fade-up transition-colors overflow-hidden">
        <div className="w-1/2 self-stretch bg-gradient-to-br from-brand-600 via-brand-700 to-slate-900 dark:from-brand-800 dark:via-slate-900 dark:to-black p-16 hidden lg:flex flex-col justify-between relative overflow-hidden shadow-2xl">
          <div className="z-10 text-white">
            <Logo
              size={52}
              showText
              variant="hero"
              className="mb-8"
              onClick={() => setCurrentPage('landing')}
            />
            <h2 className="text-3xl font-extrabold leading-snug max-w-md tracking-tight">
              Her kulaç bir hikâye yazar. Yolculuğunuzu birlikte planlayın, gelişiminizi adım adım izleyin.
            </h2>
          </div>
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://images.unsplash.com/photo-1530549387789-4c1017266635?q=80&w=2070')] opacity-20 bg-cover bg-center mix-blend-overlay" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 to-transparent pointer-events-none" />
        </div>

        <div className="w-full lg:w-1/2 self-stretch flex items-center justify-center p-4 sm:p-8 relative overflow-y-auto app-main-bg">
          <button
            onClick={() => setCurrentPage('landing')}
            className="absolute z-20 top-[calc(1rem_+_env(safe-area-inset-top))] left-[max(1rem,env(safe-area-inset-left))] sm:top-[calc(2rem_+_env(safe-area-inset-top))] sm:left-8 min-h-11 px-2 -mx-2 text-slate-500 font-bold flex items-center gap-2 transition-colors hover:text-slate-800 dark:hover:text-white"
          >
            <ArrowLeft size={20} /><span className="hidden sm:inline">Ana Sayfa</span>
          </button>
          <div className="absolute z-20 top-[calc(1rem_+_env(safe-area-inset-top))] right-[max(1rem,env(safe-area-inset-right))] sm:top-[calc(2rem_+_env(safe-area-inset-top))] sm:right-8">
            <ThemeToggle isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
          </div>

          <div className="w-full max-w-md app-surface p-6 sm:p-10 rounded-3xl transition-colors my-auto mt-[calc(4rem_+_env(safe-area-inset-top))] sm:mt-auto">
              <>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-800 dark:text-white mb-2 tracking-tight">
                  {authMode === 'login' ? 'Hoşgeldiniz' : 'Aramıza Katılın'}
                </h2>
                <p className="text-sm sm:text-base text-slate-400 dark:text-slate-300 mb-6 font-medium">
                  {authMode === 'login'
                    ? (isStudentLogin
                      ? 'E-posta adresinize gelen kodla şifresiz giriş.'
                      : 'E-posta kodu veya SMS kodu ile şifresiz giriş.')
                    : isStudentSignup
                      ? 'Formu doldurun; eğitmeniniz onayladığında e-posta ile bilgilendirileceksiniz.'
                      : 'Eğitmen hesabınız anında aktif olur; giriş e-posta veya SMS koduyla yapılır.'}
                </p>
                <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-xl mb-6 relative transition-colors">
                  <button
                    type="button"
                    onClick={() => {
                      if (!INSTRUCTOR_REGISTRATION_ENABLED) setAuthMode('login');
                      setAuthRole(USER_ROLE.INSTRUCTOR);
                      resetOtpState();
                    }}
                    className={`flex-1 py-3 rounded-lg text-sm font-bold transition-all z-10 ${
                      authRole === USER_ROLE.INSTRUCTOR
                        ? 'bg-white dark:bg-slate-700 shadow-md text-blue-600 dark:text-blue-400'
                        : 'text-slate-500'
                    }`}
                  >
                    Eğitmen
                  </button>
                  <button
                    type="button"
                    disabled={authMode === 'register' && !STUDENT_REGISTRATION_ENABLED}
                    onClick={() => {
                      if (!STUDENT_REGISTRATION_ENABLED) setAuthMode('login');
                      setAuthRole(USER_ROLE.STUDENT);
                      resetOtpState();
                    }}
                    className={`flex-1 py-3 rounded-lg text-sm font-bold transition-all z-10 disabled:opacity-40 ${
                      authRole === USER_ROLE.STUDENT
                        ? 'bg-white dark:bg-slate-700 shadow-md text-blue-600 dark:text-blue-400'
                        : 'text-slate-500'
                    }`}
                  >
                    Öğrenci
                  </button>
                </div>

                {isStudentSignup ? (
                  <StudentSignupForm
                    agreedToTerms={agreedToTerms}
                    setAgreedToTerms={setAgreedToTerms}
                    onOpenTerms={() => setTermsModalOpen(true)}
                    onOpenKvkk={() => window.open('/kvkk', '_blank', 'noopener')}
                    showToast={showToast}
                    onBackToLogin={(email) => {
                      setAuthMode('login');
                      if (email) setLoginId(email);
                      resetOtpState();
                    }}
                  />
                ) : (
                <form
                  onSubmit={handleAuthAction}
                  className="space-y-4"
                  noValidate={authMode === 'login'}
                >
                  {authMode === 'register' && (
                    <>
                      <div>
                        <label className="text-xs font-bold text-slate-400 uppercase">Ad Soyad</label>
                        <div className="relative mt-2">
                          <User className="absolute left-4 top-3.5 text-slate-300" size={20} />
                          <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            aria-label="Ad Soyad"
                            className="w-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white py-3.5 pr-4 pl-12 rounded-xl mt-1 outline-none focus:border-blue-500 transition-colors"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-slate-400 uppercase">
                          E-posta veya telefon
                        </label>
                        <div className="relative mt-2">
                          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none z-10">
                            <Mail
                              size={18}
                              className={registerKind === 'phone' ? 'text-slate-300/40 dark:text-slate-600' : 'text-slate-400 dark:text-slate-500'}
                              aria-hidden
                            />
                            <Phone
                              size={18}
                              className={registerKind === 'email' ? 'text-slate-300/40 dark:text-slate-600' : 'text-slate-400 dark:text-slate-500'}
                              aria-hidden
                            />
                          </div>
                          {!registerLoginId && otpStep === 'enter' && (
                            <span
                              aria-hidden
                              className="pointer-events-none absolute inset-y-0 left-16 right-4 flex items-center select-none text-sm font-medium text-slate-400/70 dark:text-slate-500/80 z-10 truncate"
                            >
                              Mail veya telefon numarası giriniz
                            </span>
                          )}
                          <input
                            type="text"
                            value={registerLoginId}
                            onChange={(e) => {
                              setRegisterLoginId(e.target.value);
                              if (otpStep === 'code') resetOtpState();
                            }}
                            disabled={otpStep === 'code'}
                            required
                            aria-label="Kayıt e-posta veya telefon"
                            className="w-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white py-3.5 pr-4 pl-16 rounded-xl mt-1 outline-none focus:border-blue-500 transition-colors disabled:opacity-70 relative z-[1]"
                          />
                        </div>
                      </div>
                      {otpStep === 'code' && (
                        <div>
                          <label className="text-xs font-bold text-slate-400 uppercase">
                            {otpKind === 'email' ? 'E-posta kodu' : 'SMS kodu'}
                          </label>
                          <div className="relative mt-2">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 z-10" size={20} />
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              value={otpCode}
                              onChange={handleOtpInput}
                              required
                              maxLength={6}
                              aria-label={otpKind === 'email' ? 'E-posta kodu' : 'SMS kodu'}
                              className="w-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white py-3.5 pr-4 pl-12 rounded-xl outline-none focus:border-blue-500 transition-colors tracking-widest relative z-[1]"
                            />
                          </div>
                          <button
                            type="button"
                            disabled={isProcessing}
                            onClick={resetOtpState}
                            className="mt-2 text-xs font-bold text-blue-600 dark:text-blue-400"
                          >
                            {otpKind === 'email' ? 'E-postayı değiştir' : 'Numarayı değiştir'}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {authMode === 'login' && (
                    <>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none z-10">
                          <Mail
                            size={18}
                            className={loginKind === 'phone' && !isStudentLogin ? 'text-slate-300/40 dark:text-slate-600' : 'text-slate-400 dark:text-slate-500'}
                            aria-hidden
                          />
                          {!isStudentLogin && (
                            <Phone
                              size={18}
                              className={loginKind === 'email' ? 'text-slate-300/40 dark:text-slate-600' : 'text-slate-400 dark:text-slate-500'}
                              aria-hidden
                            />
                          )}
                        </div>
                        {!loginId && otpStep === 'enter' && (
                          <span
                            aria-hidden
                            className={`pointer-events-none absolute inset-y-0 ${isStudentLogin ? 'left-11' : 'left-16'} right-4 flex items-center select-none text-sm sm:text-[15px] font-medium text-slate-400/70 dark:text-slate-500/80 z-10 truncate`}
                          >
                            {isStudentLogin ? 'E-posta adresinizi giriniz' : 'Mail veya telefon numarası giriniz'}
                          </span>
                        )}
                        <input
                          type="text"
                          value={loginId}
                          onChange={(e) => {
                            setLoginId(e.target.value);
                            if (otpStep === 'code') resetOtpState();
                          }}
                          disabled={otpStep === 'code'}
                          autoComplete={isStudentLogin ? 'email' : 'username'}
                          inputMode="email"
                          aria-label={isStudentLogin ? 'E-posta' : 'E-posta veya telefon'}
                          className={`w-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white py-3.5 pr-4 ${isStudentLogin ? 'pl-11' : 'pl-16'} rounded-xl outline-none focus:border-blue-500 transition-colors disabled:opacity-70 relative z-[1]`}
                        />
                      </div>
                      {otpStep === 'enter' && (
                        <EmailSuggestion email={loginId} onApply={setLoginId} className="-mt-1" />
                      )}

                      {otpStep === 'code' && (
                        <div>
                          <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 z-10" size={20} />
                            {!otpCode && (
                              <span
                                aria-hidden
                                className="pointer-events-none absolute inset-y-0 left-12 right-4 flex items-center select-none text-[15px] font-medium tracking-[0.35em] text-slate-300/60 dark:text-slate-600/70 z-10"
                              >
                                • • • • • •
                              </span>
                            )}
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              value={otpCode}
                              onChange={handleOtpInput}
                              required
                              maxLength={6}
                              aria-label={otpKind === 'email' ? 'E-posta kodu' : 'SMS kodu'}
                              className="w-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white py-3.5 pr-4 pl-12 rounded-xl outline-none focus:border-blue-500 transition-colors tracking-widest relative z-[1]"
                            />
                          </div>
                          <div className="mt-2 flex items-center justify-between gap-3">
                            <button
                              type="button"
                              disabled={isProcessing}
                              onClick={resetOtpState}
                              className="text-xs font-bold text-blue-600 dark:text-blue-400"
                            >
                              {otpKind === 'email' ? 'E-postayı değiştir' : 'Numarayı değiştir'}
                            </button>
                            <button
                              type="button"
                              disabled={isProcessing}
                              onClick={() => (otpKind === 'email'
                                ? handleSendEmailCode(loginId)
                                : handleSendPhoneCode(loginId))}
                              className="text-xs font-bold text-blue-600 dark:text-blue-400"
                            >
                              Kodu tekrar gönder
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {authMode === 'register' && (
                    <div className="space-y-3 mt-4 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <div className="relative flex items-center justify-center mt-0.5 shrink-0">
                          <input
                            type="checkbox"
                            required
                            checked={agreedToTerms}
                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                            className="peer appearance-none w-5 h-5 border-2 border-slate-300 dark:border-slate-600 rounded-md checked:bg-blue-600 checked:border-blue-600 transition-colors cursor-pointer"
                          />
                          <Check
                            size={14}
                            className="absolute text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
                          />
                        </div>
                        <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-tight select-none mt-0.5">
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTermsModalOpen(true); }}
                            className="text-blue-600 dark:text-blue-400 font-bold hover:underline"
                          >
                            Kullanıcı Sözleşmesi
                          </button>
                          'ni okudum ve kabul ediyorum.
                        </span>
                      </label>
                    </div>
                  )}

                  {otpStep === 'code' && otpKind === 'email' && (
                    <p className="text-emerald-600 dark:text-emerald-400 text-sm font-bold bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/50 p-3 rounded-xl flex items-start gap-2.5">
                      <CheckCircle size={18} className="shrink-0 mt-0.5" />
                      Kod e-postanıza gönderildi. Gelen 6 haneli kodu girin. Birkaç dakikada gelmezse Spam / Gereksiz klasörüne bakın.
                    </p>
                  )}

                  <button
                    disabled={isProcessing}
                    className="w-full bg-gradient-to-r from-brand-500 to-brand-700 hover:from-brand-600 hover:to-brand-800 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-instructor-nav mt-6 disabled:opacity-70"
                  >
                    {isProcessing ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      submitLabel
                    )}
                    {!isProcessing && <ArrowRight size={20} />}
                  </button>

                  <div className="flex flex-col sm:flex-row justify-between items-center text-sm pt-4 border-t border-slate-100 dark:border-slate-800 mt-4 gap-2">
                    {authMode === 'login' ? (
                      INSTRUCTOR_REGISTRATION_ENABLED && authRole === USER_ROLE.INSTRUCTOR ? (
                        <button
                          type="button"
                          onClick={() => {
                            setAuthMode('register');
                            setAuthRole(USER_ROLE.INSTRUCTOR);
                            resetOtpState();
                          }}
                          className="font-bold text-blue-600 mx-auto"
                        >
                          Yeni Eğitmen Hesabı Aç
                        </button>
                      ) : authRole === USER_ROLE.STUDENT ? (
                        STUDENT_REGISTRATION_ENABLED ? (
                          <button
                            type="button"
                            onClick={() => {
                              setAuthMode('register');
                              resetOtpState();
                            }}
                            className="font-bold text-blue-600 mx-auto"
                          >
                            Hesabın yok mu? Öğrenci olarak kayıt ol
                          </button>
                        ) : (
                          <p className="text-xs text-slate-400 text-center mx-auto font-medium">
                            Öğrenci kaydı kapalı. Hesabınızı eğitmeniniz oluşturur.
                          </p>
                        )
                      ) : (
                        <p className="text-xs text-slate-400 text-center mx-auto font-medium">
                          {INSTRUCTOR_REGISTRATION_CLOSED_HINT}
                        </p>
                      )
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setAuthMode('login');
                          resetOtpState();
                        }}
                        className="font-bold text-slate-500 hover:text-blue-600 mx-auto"
                      >
                        Zaten hesabım var, Giriş Yap
                      </button>
                    )}
                  </div>
                </form>
                )}
              </>
          </div>
        </div>
      </div>

      <Modal title="Kullanıcı Sözleşmesi" isOpen={termsModalOpen} onClose={() => setTermsModalOpen(false)}>
        <div className="space-y-4 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          <p><strong>1. Taraflar:</strong> İşbu sözleşme, Atletik Yüzme platformunu kullanan eğitmen ve öğrenciler ile Atletik Yüzme yönetimi arasında geçerlidir.</p>
          <p><strong>2. Hizmet Şartları:</strong> Kullanıcılar, platformu yalnızca yasal ve platformun amacına uygun (spor koçluğu) şekilde kullanmayı kabul ederler.</p>
          <p><strong>3. Hesap Güvenliği:</strong> Kullanıcılar kendi hesap bilgilerinin ve e-posta / SMS kodlarının güvenliğinden bizzat sorumludur.</p>
          <p><strong>4. İptal ve Fesih:</strong> Kullanıcılar istedikleri zaman hesaplarını kalıcı olarak silme hakkına sahiptir.</p>
          <p><strong>5. Kişisel Veriler:</strong> Kişisel verilerinizin nasıl işlendiğine dair detaylı bilgi için lütfen <button type="button" onClick={() => { setTermsModalOpen(false); setCurrentPage('kvkk'); }} className="text-blue-600 dark:text-blue-400 underline underline-offset-2 font-semibold hover:text-blue-700 transition-colors">KVKK Aydınlatma Metni</button>'ni okuyunuz.</p>
          <button
            type="button"
            onClick={() => { setAgreedToTerms(true); setTermsModalOpen(false); }}
            className="w-full mt-6 bg-gradient-to-r from-brand-500 to-brand-700 hover:from-brand-600 hover:to-brand-800 text-white py-3.5 rounded-xl font-bold transition-all shadow-instructor-nav"
          >
            Okudum ve Onaylıyorum
          </button>
        </div>
      </Modal>

      <div
        id="recaptcha-container"
        className="mt-3 flex justify-center min-h-[78px]"
      />
    </>
  );
};

export default LoginScreen;
